import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, LogOut, Menu } from 'lucide-react';
import SinoAvisos from './home/SinoAvisos';
import BotaoAgenda from './home/BotaoAgenda';
import { interfaceMinima } from '../utils/perfisRestritos';

// =====================================================================
// CABEÇALHO PADRÃO DOS MÓDULOS
// =====================================================================
// Mesmo desenho da barra superior da Home, em versão ENXUTA (sem busca/sino,
// que são cascas): à esquerda o título do módulo; à direita o ícone do Manual
// da Tela, o usuário (avatar + nome/perfil) e o Sair.
//
// É recolorido por módulo via prop `cor` (a cor de destaque do módulo — ex.:
// roxo no Fiscal). Lê o usuário do localStorage e faz o logout aqui mesmo.
//
// Props:
//   - eyebrow   : rótulo pequeno acima do título (ex.: "Fiscal")
//   - titulo    : título do módulo (ex.: "Módulo Fiscal")
//   - cor       : cor de destaque do módulo (ícones, avatar)
//   - onAbrirManual : abre o Manual da Tela (o módulo é dono do HelpCenter, com
//                     seus verbetes próprios — por isso o callback)
//   - onAbrirMenu : abre a Sidebar no mobile (hambúrguer dentro do cabeçalho).
//                   O módulo dono do estado da Sidebar passa () => setIsCollapsed(false).
//   - isMobile  : no celular mostra o hambúrguer + título, esconde avatar/usuário e
//                 compacta o cabeçalho (mesmo padrão da Home mobile).

const FONTE = "'Raleway', sans-serif";

function ModuloHeader({ eyebrow, titulo, cor, onAbrirManual, onAbrirMenu, isMobile }) {
    const navigate = useNavigate();

    // Lido de forma SÍNCRONA (e não num useEffect) porque o perfil decide o que
    // o cabeçalho mostra: com leitura tardia, o sino e a agenda apareceriam no
    // primeiro render e sumiriam logo depois — uma piscada visível.
    const [user] = useState(() => {
        try {
            const s = localStorage.getItem('user');
            return s ? JSON.parse(s) : null;
        } catch { return null; }
    });

    const inicial = user && user.nome ? user.nome.charAt(0).toUpperCase() : '?';

    // Perfil preso a UMA tela não navega: sino e agenda saem do cabeçalho.
    // Ver o porquê em utils/perfisRestritos.js › interfaceMinima.
    const enxuto = interfaceMinima(user?.perfil);

    const handleLogout = () => {
        // ⚠️ CONFIRMAÇÃO só na interface enxuta, e por um motivo específico:
        //    esses perfis têm SESSÃO LONGA (1 ano) e usam o portal pelo atalho
        //    do celular, onde o toque errado é fácil. Para os demais — sessão de
        //    8h, login diário de qualquer forma — a confirmação seria atrito à toa.
        //
        //    O botão NÃO foi removido de propósito: com sessão de 1 ano, ele é o
        //    único jeito de a própria pessoa encerrar o acesso (emprestou o
        //    aparelho, desconfiou de algo). Sem ele, só o logout forçado pelo
        //    Painel Admin — ou seja, dependeria de ligar para a equipe de dados.
        if (enxuto && !window.confirm('Sair do portal?\n\nVocê precisará entrar novamente com usuário e senha.')) {
            return;
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const btnIcone = { background: 'none', border: 'none', cursor: 'pointer', color: cor, display: 'flex', alignItems: 'center', padding: '6px' };

    return (
        <div style={{
            height: '85px', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 30px', position: 'sticky', top: 0, zIndex: 30, fontFamily: FONTE,
        }}>
            {/* Esquerda: hambúrguer (só no mobile, abre a Sidebar) + título do módulo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                {isMobile && (
                    <button onClick={onAbrirMenu} title="Menu" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', padding: '4px', flexShrink: 0 }}>
                        <Menu size={26} />
                    </button>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{eyebrow}</span>
                    {/* ⚠️ `lining-nums`: a Raleway vem com algarismos "old-style" por
                        padrão (alturas variadas, os números saem menores que as
                        maiúsculas — visível em títulos como "DEM-00002"). Isto força
                        os algarismos a terem a mesma altura das letras. */}
                    <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'lining-nums' }}>{titulo}</h2>
                </div>
            </div>

            {/* Ações: sino · agenda · manual · usuário · sair */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '18px', flexShrink: 0 }}>
                {/* Sino e agenda somem na interface enxuta: para quem enxerga uma
                    tela só, o sino abre lista sempre vazia (avisos são mirados por
                    perfil) e a agenda dispara a vinculação com o M365 — fluxo que
                    quem só vem ler um documento não pediu. */}
                {!enxuto && (
                    <>
                        {/* Sino de avisos (badge + lista + pop-up do aviso) — recolorido pela cor do módulo */}
                        <SinoAvisos cor={cor} tamanhoIcone={24} larguraLista={300} topoLista={46} />

                        {/* Agenda Corporativa (Teams/M365) num modal — a mesma da Home,
                            alcançável de dentro de qualquer módulo. */}
                        <BotaoAgenda cor={cor} tamanhoIcone={24} />
                    </>
                )}

                {/* Só aparece se o módulo tiver Manual. Módulo sem verbete (ex.: Projetos)
                    não passa o callback — e botão que não faz nada ao ser clicado é bug,
                    não decoração. */}
                {onAbrirManual && (
                    <button onClick={onAbrirManual} title="Manual da Tela" style={btnIcone}>
                        <HelpCircle size={24} />
                    </button>
                )}

                {/* Divisória + usuário (avatar + nome/perfil) — só no desktop; no mobile
                    o cabeçalho fica enxuto (hambúrguer + título à esquerda). */}
                {!isMobile && <div style={{ width: '1px', height: '40px', backgroundColor: '#e2e8f0' }} />}

                {!isMobile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: cor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>
                            {inicial}
                        </div>
                        <div style={{ lineHeight: 1.25 }}>
                            <div style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '12px' }}>{user ? user.nome : ''}</div>
                            <div style={{ fontWeight: 300, color: '#555', fontSize: '10px' }}>{user ? user.perfil : ''}</div>
                        </div>
                    </div>
                )}

                <button onClick={handleLogout} title="Sair" style={btnIcone}>
                    <LogOut size={22} />
                </button>
            </div>
        </div>
    );
}

export default ModuloHeader;
